// src/js/widgets/manager-requests/composables/useRequestData.ts

import { ref, watch } from 'vue';
import type {
  Request,
  RequestStatus,
  UpdateStatusPayload,
  SendReplyPayload,
  ApiResponse,
  Reply,
} from '../types';
import { useUserRole } from './useUserRole';
import { getApiUrl } from '@/js/utils/getApiUrl';
import { normalizeStatus, statusToApi } from '../types';

interface RawApplication {
  id: string | number;
  createdAt: string;
  name?: string;
  status: string;
  replies?: Array<{
    id: string | number;
    message: string;
  }>;
  companyName?: string;
  email?: string;
}

interface RawApplicationDetails {
  id: string | number;
  createdAt: string;
  companyName?: string;
  email?: string;
  status: string;
  text?: string;
  files?: Request['files'];
  history?: Array<{ date: string; status: string }>;
  fields?: Array<{ fieldName: string; fieldValue: string }>;
}

async function fetchGet<T>(url: string): Promise<ApiResponse<T>> {
  const response = await fetch(url);
  if (!response.ok) {
    throw new Error(`HTTP ${response.status}`);
  }
  const text = await response.text();
  try {
    return JSON.parse(text);
  } catch {
    throw new Error('Сервер вернул недопустимый формат ответа');
  }
}

export function useRequestData() {
  const requests = ref<Request[]>([]);
  const loading = ref(false);
  const error = ref<string | null>(null);

  const { apiType, error: roleError } = useUserRole();

  watch(roleError, (err) => {
    if (err) {
      error.value = err;
    }
  });

  const loadRequests = async (filters: { status?: RequestStatus[] } = {}) => {
    if (!apiType.value) {
      error.value = 'Не удалось определить тип заявок';
      return;
    }

    loading.value = true;
    error.value = null;

    try {
      let url = getApiUrl(`/api/getApplications.php?type=${apiType.value}`);
      if (filters.status && filters.status.length > 0) {
        const params = new URLSearchParams();
        filters.status.forEach((status) => {
          const apiStatus = statusToApi[status];
          params.append('status', apiStatus);
        });
        url += `&${params.toString()}`;
      }

      const result = await fetchGet<{ applications: RawApplication[] }>(url);

      if (!result.success) {
        throw new Error(result.msg);
      }

      const applications = result.data?.applications || [];
      const data: Request[] = applications.map((item) => {
        const id = typeof item.id === 'string' ? parseInt(item.id, 10) : item.id;
        const replies: Reply[] = (item.replies || []).map((r) => ({
          id: typeof r.id === 'string' ? parseInt(r.id, 10) : r.id,
          message: r.message || '',
        }));

        return {
          id,
          createdAt: item.createdAt || '',
          email: '',
          companyName: item.companyName || '',
          status: normalizeStatus(item.status),
          text: '',
          files: [],
          history: [],
          replies,
          fields: [],
        };
      });

      data.sort((a, b) => new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime());
      requests.value = data;
    } catch (err) {
      const message = err instanceof Error ? err.message : 'Неизвестная ошибка';
      error.value = message;
      console.error('Ошибка загрузки заявок:', err);
    } finally {
      loading.value = false;
    }
  };

  const loadRequestDetails = async (id: number): Promise<Request | null> => {
    if (!apiType.value) throw new Error('Тип заявки не определён');

    try {
      const url = getApiUrl(`/api/getApplicationDetails.php?type=${apiType.value}&id=${id}`);
      const result = await fetchGet<RawApplicationDetails>(url);

      if (!result.success) {
        throw new Error(result.msg);
      }

      const raw = result.data;
      if (!raw) {
        throw new Error('Данные заявки отсутствуют');
      }

      const history = (raw.history || []).map((h) => ({
        date: h.date,
        status: normalizeStatus(h.status),
      }));

      const fields = (raw.fields || []).map((f) => ({
        fieldName: f.fieldName || '',
        fieldValue: f.fieldValue || '',
      }));

      const request: Request = {
        id: typeof raw.id === 'string' ? parseInt(raw.id, 10) : raw.id,
        createdAt: raw.createdAt || '',
        email: raw.email || '',
        companyName: raw.companyName || '',
        status: normalizeStatus(raw.status),
        text: raw.text || '',
        files: raw.files || [],
        history,
        replies: [],
        fields,
      };

      return request;
    } catch (err) {
      console.error('Ошибка загрузки деталей:', err);
      return null;
    }
  };

  const updateStatus = async (id: number, payload: UpdateStatusPayload) => {
    if (!apiType.value) throw new Error('Тип заявки не определён');

    loading.value = true;
    error.value = null;

    try {
      const formData = new FormData();
      formData.append('type', apiType.value);
      formData.append('id', String(id));
      formData.append('status', statusToApi[payload.status]);

      const response = await fetch(getApiUrl('/api/updateApplication.php'), {
        method: 'POST',
        body: formData,
      });

      if (!response.ok) {
        throw new Error(`HTTP ${response.status}`);
      }

      const text = await response.text();
      let result;
      try {
        result = JSON.parse(text);
      } catch {
        throw new Error('Сервер вернул недопустимый формат');
      }

      if (!result.success) {
        throw new Error(result.msg || 'Не удалось обновить статус');
      }

      const request = requests.value.find((r) => r.id === id);
      if (request) {
        request.status = payload.status;
      }
    } catch (err) {
      const message = err instanceof Error ? err.message : 'Неизвестная ошибка';
      error.value = message;
      console.error('Ошибка обновления статуса:', err);
      throw err;
    } finally {
      loading.value = false;
    }
  };

  const sendReply = async (id: number, payload: SendReplyPayload) => {
    if (!apiType.value) throw new Error('Тип заявки не определён');

    loading.value = true;
    error.value = null;

    try {
      const formData = new FormData();
      formData.append('type', apiType.value);
      formData.append('id', String(id));
      formData.append('message', payload.message);

      const response = await fetch(getApiUrl('/api/replyApplication.php'), {
        method: 'POST',
        body: formData,
      });

      if (!response.ok) {
        throw new Error(`HTTP ${response.status}`);
      }

      const text = await response.text();
      let result;
      try {
        result = JSON.parse(text);
      } catch {
        throw new Error('Сервер вернул недопустимый формат');
      }

      if (!result.success) {
        throw new Error(result.msg || 'Не удалось отправить ответ');
      }

      const request = requests.value.find((r) => r.id === id);
      if (request && request.replies) {
        request.replies.push({
          id: Date.now(),
          message: payload.message,
        });
      }
    } catch (err) {
      const message = err instanceof Error ? err.message : 'Неизвестная ошибка';
      error.value = message;
      console.error('Ошибка отправки ответа:', err);
      throw err;
    } finally {
      loading.value = false;
    }
  };

  const uploadFiles = async (id: number, files: File[]) => {
    if (!apiType.value) throw new Error('Тип заявки не определён');
    if (files.length === 0) return;

    loading.value = true;
    error.value = null;

    try {
      const formData = new FormData();
      formData.append('type', apiType.value);
      formData.append('id', String(id));

      // Добавляем файлы с правильным ключом
      files.forEach((file) => {
        formData.append('replyAttachments[]', file);
      });

      // Используем ТОТ ЖЕ эндпоинт, что и для ответа
      const response = await fetch(getApiUrl('/api/updateApplication.php'), {
        method: 'POST',
        body: formData,
      });

      if (!response.ok) {
        throw new Error(`HTTP ${response.status}`);
      }

      const text = await response.text();
      let result;
      try {
        result = JSON.parse(text);
      } catch {
        throw new Error('Сервер вернул недопустимый формат');
      }

      if (!result.success) {
        throw new Error(result.msg || 'Не удалось загрузить файлы');
      }

      return result;
    } catch (err) {
      const message = err instanceof Error ? err.message : 'Неизвестная ошибка';
      error.value = message;
      console.error('Ошибка загрузки файлов:', err);
      throw err;
    } finally {
      loading.value = false;
    }
  };

  const deleteRequest = async (id: number) => {
    if (!apiType.value) {
      error.value = 'Не удалось определить тип заявок';
      return;
    }

    loading.value = true;
    error.value = null;

    try {
      const response = await fetch(
        getApiUrl(`/api/removeApplication.php?type=${apiType.value}&id=${id}`),
        {
          method: 'DELETE',
        },
      );

      if (!response.ok) {
        throw new Error(`HTTP ${response.status}`);
      }

      const text = await response.text();
      let result;
      try {
        result = JSON.parse(text);
      } catch {
        throw new Error('Сервер вернул недопустимый формат');
      }

      if (!result.success) {
        throw new Error(result.msg || 'Не удалось удалить заявку');
      }

      requests.value = requests.value.filter((r) => r.id !== id);
    } catch (err) {
      const message = err instanceof Error ? err.message : 'Неизвестная ошибка';
      error.value = message;
      console.error('Ошибка удаления заявки:', err);
      throw err;
    } finally {
      loading.value = false;
    }
  };

  watch(apiType, (newType) => {
    if (newType) {
      loadRequests({ status: [] });
    }
  });

  return {
    requests,
    loading,
    error,
    loadRequests,
    loadRequestDetails,
    updateStatus,
    sendReply,
    uploadFiles,
    deleteRequest,
  };
}
